import React, { useEffect, useState, useMemo } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Spinner, Field, Input, Select, Modal } from '../ui.jsx';

const STATUS_COLOR = { Scheduled:G.blue, Booked:G.cyan, Dispatched:G.purple, 'En Route':G.cyan, 'On Site':G.orange, Completed:G.green, 'Needs Attention':G.yellow, Cancelled:G.red };

export function Calendar({ toast, go }) {
  const [jobs, setJobs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [techs, setTechs] = useState([]);
  const [gcalEvents, setGcalEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('month');
  const [refDate, setRefDate] = useState(new Date());
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [visibleCalIds, setVisibleCalIds] = useState(null); // null = show all
  const [managingCals, setManagingCals] = useState(false);

  const loadCalendars = () => api('/api/calendars').then(setCalendars).catch(()=>{});

  const load = async () => {
    setLoading(true);
    // Run every fetch independently. The OLD code wrapped jobs+contacts+techs+
    // calendars+gcal in a Promise.all where any failure (e.g. super_admin
    // without ghosting → /api/jobs 403) aborted the entire load — including
    // the gcal request — so the Calendar tab showed empty with no events.
    api('/api/jobs').then(setJobs).catch(e => { toast?.(e.message, 'error'); setJobs([]); });
    api('/api/contacts').then(setContacts).catch(()=>setContacts([]));
    api('/api/techs').then(setTechs).catch(()=>setTechs([]));
    api('/api/calendars').then(setCalendars).catch(()=>setCalendars([]));
    const start = new Date(refDate.getFullYear(), refDate.getMonth(), 1).toISOString();
    const end = new Date(refDate.getFullYear(), refDate.getMonth()+1, 0, 23, 59, 59).toISOString();
    api(`/api/integrations/gcal/events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`)
      .then(r => setGcalEvents(r.events || []))
      .catch(() => setGcalEvents([]));
    setLoading(false);
  };
  useEffect(() => { load(); }, [refDate.getMonth(), refDate.getFullYear()]);

  if (loading) return <div style={{ display:'flex', justifyContent:'center', padding:80 }}><Spinner sz={32}/></div>;

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, gap:14, flexWrap:'wrap' }}>
      <div>
        <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Calendar</h1>
        {gcalEvents.length > 0 && (
          <div style={{ fontSize:12, color:G.textMuted, marginTop:4, display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ width:8, height:8, borderRadius:99, background:'#4285f4' }}/>
            {gcalEvents.length} event{gcalEvents.length===1?'':'s'} synced from Google Calendar this month
          </div>
        )}
      </div>
      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
        <div style={{ display:'flex', gap:6, background:G.input, padding:4, borderRadius:10 }}>
          <Btn sz="sm" v={view==='month'?'primary':'ghost'} onClick={()=>setView('month')}>Month</Btn>
          <Btn sz="sm" v={view==='week'?'primary':'ghost'} onClick={()=>setView('week')}>Week</Btn>
        </div>
        <Btn sz="sm" v="cyan" onClick={async ()=>{
          const today = new Date().toISOString().slice(0,10);
          const techId = prompt('Optimize route for which tech? (paste tech ID, or leave blank for all):', '');
          try {
            const r = await api('/api/ai-tools/route-optimize', { method:'POST', body: { date: today, techId: techId||undefined, persist: false }});
            if (!r.ordered.length) return toast('No jobs to optimize', 'warn');
            const m = `Optimized order:\n${r.ordered.map((j,i)=>`${i+1}. ${j.scheduledTime||''} ${j.name}`).join('\n')}\n\nCurrent ~${(r.currentDistanceM/1609).toFixed(1)} mi → Optimized ${(r.optimizedDistanceM/1609).toFixed(1)} mi (saved ${r.savedMiles} mi).\n\nApply these times? (Cancel to keep current.)`;
            if (confirm(m)) {
              await api('/api/ai-tools/route-optimize', { method:'POST', body: { date: today, techId: techId||undefined, persist: true }});
              load(); toast(`Optimized — saved ${r.savedMiles} mi`);
            }
          } catch(e) { toast(e.message,'error'); }
        }}>🗺️ Optimize Route</Btn>
        <Btn sz="sm" onClick={()=>setManagingCals(true)}>📚 Calendars</Btn>
        <Btn v="primary" onClick={()=>setEditing({ status:'Scheduled', durationMinutes:120, scheduledDate: new Date().toISOString().slice(0,10), startTime:'09:00' })}>+ New Job</Btn>
      </div>
    </div>

    {calendars.length > 0 && (
      <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
        <span style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', fontWeight:700, letterSpacing:'.05em' }}>Calendars:</span>
        {calendars.map(c => {
          const on = !visibleCalIds || visibleCalIds.includes(c.id);
          return <button key={c.id} onClick={()=>{
            const cur = visibleCalIds || calendars.map(x => x.id);
            const next = cur.includes(c.id) ? cur.filter(x => x !== c.id) : [...cur, c.id];
            setVisibleCalIds(next.length === calendars.length ? null : next);
          }} style={{
            padding:'4px 10px', borderRadius:14, fontSize:12, fontWeight:600,
            border:`1px solid ${on ? c.color : G.cardBorder}`,
            background: on ? c.color+'22' : 'transparent',
            color: on ? c.color : G.textMuted, cursor:'pointer',
            display:'flex', alignItems:'center', gap:6,
          }}>
            <span style={{ width:8, height:8, borderRadius:4, background:c.color }}/>
            {c.name}
          </button>;
        })}
        {visibleCalIds && <button onClick={()=>setVisibleCalIds(null)} style={{ background:'none', border:'none', color:G.textMuted, fontSize:11, cursor:'pointer' }}>Show all</button>}
      </div>
    )}

    {(() => {
      const filteredJobs = visibleCalIds
        ? jobs.filter(j => visibleCalIds.includes(j.calendarId) || (!j.calendarId && visibleCalIds.includes((calendars.find(c=>c.isDefault)||{}).id)))
        : jobs;
      const reschedule = async (jobId, newDate) => {
        try {
          await api(`/api/jobs/${jobId}`, { method:'PATCH', body:{ scheduledDate: newDate } });
          load();
          toast(`✓ Moved to ${newDate}`);
        } catch (e) { toast(e.message, 'error'); }
      };
      return view==='month'
        ? <MonthGrid jobs={filteredJobs} gcalEvents={gcalEvents} refDate={refDate} setRefDate={setRefDate} calendars={calendars} onReschedule={reschedule} onDayClick={d => setEditing({ status:'Scheduled', durationMinutes:120, scheduledDate: d.toISOString().slice(0,10), startTime:'09:00' })} onJobClick={j=>setViewing(j)}/>
        : <WeekGrid jobs={filteredJobs} gcalEvents={gcalEvents} refDate={refDate} setRefDate={setRefDate} calendars={calendars} onReschedule={reschedule} onDayClick={d => setEditing({ status:'Scheduled', durationMinutes:120, scheduledDate: d.toISOString().slice(0,10), startTime:'09:00' })} onJobClick={j=>setViewing(j)}/>;
    })()}

    {managingCals && <CalendarsManagerModal calendars={calendars} onClose={()=>setManagingCals(false)} onChanged={()=>loadCalendars()} toast={toast}/>}

    <div style={{ ...styles.card, marginTop:14, padding:14, fontSize:13, color:G.textMuted, lineHeight:1.5 }}>
      💡 Click any day to schedule a new job. Click a job to view, advance status, or message the customer.
    </div>

    <NewJobModal job={editing} contacts={contacts} techs={techs} onClose={()=>setEditing(null)} onSaved={()=>{ load(); setEditing(null); toast('Job scheduled'); }} toast={toast}/>
    <JobDetailModal job={viewing} onClose={()=>setViewing(null)} onSaved={()=>{ load(); setViewing(null); toast('Job updated'); }} toast={toast}/>
  </div>;
}

function MonthGrid({ jobs, gcalEvents = [], refDate, setRefDate, onDayClick, onJobClick, calendars = [], onReschedule }) {
  const m = refDate.getMonth(); const y = refDate.getFullYear();
  const first = new Date(y, m, 1); const last = new Date(y, m+1, 0);
  const startDay = first.getDay();
  const daysInMonth = last.getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const jobsByDay = useMemo(() => {
    const map = {};
    jobs.forEach(j => {
      if (!j.scheduledDate) return;
      const k = j.scheduledDate.slice(0,10);
      (map[k] = map[k] || []).push(j);
    });
    return map;
  }, [jobs]);

  const gcalByDay = useMemo(() => {
    const map = {};
    (gcalEvents || []).forEach(e => {
      if (!e.start) return;
      const k = e.start.slice(0, 10);
      (map[k] = map[k] || []).push(e);
    });
    return map;
  }, [gcalEvents]);

  const today = new Date().toISOString().slice(0,10);

  return <div>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
      <Btn onClick={()=>setRefDate(new Date(y, m-1, 1))}>← Prev</Btn>
      <h2 style={{ margin:0, fontSize:18 }}>{first.toLocaleDateString('en-US',{month:'long', year:'numeric'})}</h2>
      <Btn onClick={()=>setRefDate(new Date(y, m+1, 1))}>Next →</Btn>
    </div>
    <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', borderBottom:`1px solid ${G.border}` }}>
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => <div key={d} style={{ padding:10, fontSize:11, color:G.textMuted, fontWeight:700, textAlign:'center', textTransform:'uppercase', letterSpacing:'.05em' }}>{d}</div>)}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)' }}>
        {cells.map((cell, i) => {
          if (!cell) return <div key={i} style={{ minHeight:120, borderRight:`1px solid ${G.border}`, borderBottom:`1px solid ${G.border}`, background:G.bg }}/>;
          const k = cell.toISOString().slice(0,10);
          const dayJobs = jobsByDay[k] || [];
          const dayGcal = gcalByDay[k] || [];
          const isToday = k === today;
          return <div key={i}
            onClick={()=>onDayClick(cell)}
            onDragOver={e => { e.preventDefault(); e.currentTarget.style.background = G.purpleDim; }}
            onDragLeave={e => { e.currentTarget.style.background = isToday ? G.purpleDim : 'transparent'; }}
            onDrop={e => {
              e.preventDefault();
              e.currentTarget.style.background = isToday ? G.purpleDim : 'transparent';
              const jobId = e.dataTransfer.getData('jobId');
              const fromDate = e.dataTransfer.getData('fromDate');
              if (jobId && fromDate !== k && onReschedule) onReschedule(jobId, k);
            }}
            style={{ minHeight:120, borderRight:`1px solid ${G.border}`, borderBottom:`1px solid ${G.border}`, padding:6, background: isToday ? G.purpleDim : 'transparent', cursor:'pointer' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
              <div style={{ fontSize:12, fontWeight:700, color: isToday ? G.purple : G.textMuted }}>{cell.getDate()}</div>
              {dayJobs.length === 0 && dayGcal.length === 0 && <div style={{ fontSize:14, color:G.textDim, opacity:0 }} className="day-add">+</div>}
            </div>
            {/* TradeOps jobs first */}
            {dayJobs.slice(0,3).map(j => (
              <div key={j.id}
                draggable
                onDragStart={e => { e.dataTransfer.setData('jobId', j.id); e.dataTransfer.setData('fromDate', k); e.dataTransfer.effectAllowed = 'move'; }}
                onClick={e=>{ e.stopPropagation(); onJobClick(j); }}
                style={(() => {
                  const cal = (calendars||[]).find(c => c.id === j.calendarId);
                  const c = cal?.color || STATUS_COLOR[j.status];
                  return { background: c+'33', borderLeft:`3px solid ${c}`, padding:'4px 6px', borderRadius:4, marginBottom:3, fontSize:11, cursor:'grab', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' };
                })()}>
                {j.startTime || ''} {j.name}
              </div>
            ))}
            {/* Google Calendar events — muted style so they don't compete visually with jobs */}
            {dayGcal.slice(0,3).map(e => (
              <div key={e.id} title={e.title + (e.location ? ' · ' + e.location : '')}
                onClick={ev=>{ ev.stopPropagation(); if (e.htmlLink) window.open(e.htmlLink, '_blank'); }}
                style={{ background: 'rgba(66,133,244,.18)', borderLeft:'3px solid #4285f4', padding:'4px 6px', borderRadius:4, marginBottom:3, fontSize:11, cursor:'pointer', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'#90c2ff' }}>
                📅 {!e.isAllDay && e.start ? new Date(e.start).toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'}) + ' ' : ''}{e.title}
              </div>
            ))}
            {(dayJobs.length + dayGcal.length) > 6 && <div style={{ fontSize:10, color:G.textMuted, padding:'2px 6px' }}>+{(dayJobs.length + dayGcal.length) - 6} more</div>}
          </div>;
        })}
      </div>
    </div>
  </div>;
}

function WeekGrid({ jobs, gcalEvents = [], refDate, setRefDate, onDayClick, onJobClick, calendars = [], onReschedule }) {
  const start = new Date(refDate); start.setDate(refDate.getDate() - refDate.getDay()); start.setHours(0,0,0,0);
  const days = Array.from({length:7}, (_,i) => { const d = new Date(start); d.setDate(start.getDate()+i); return d; });
  const today = new Date().toISOString().slice(0,10);
  const jobsByDay = useMemo(() => {
    const map = {};
    jobs.forEach(j => { if (j.scheduledDate) (map[j.scheduledDate.slice(0,10)] = map[j.scheduledDate.slice(0,10)] || []).push(j); });
    return map;
  }, [jobs]);
  const gcalByDay = useMemo(() => {
    const map = {};
    (gcalEvents || []).forEach(e => {
      if (!e.start) return;
      const k = e.start.slice(0, 10);
      (map[k] = map[k] || []).push(e);
    });
    return map;
  }, [gcalEvents]);

  return <div>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
      <Btn onClick={()=>{ const d = new Date(refDate); d.setDate(d.getDate()-7); setRefDate(d); }}>← Prev Week</Btn>
      <h2 style={{ margin:0, fontSize:18 }}>{start.toLocaleDateString('en-US',{month:'short',day:'numeric'})} – {days[6].toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</h2>
      <Btn onClick={()=>{ const d = new Date(refDate); d.setDate(d.getDate()+7); setRefDate(d); }}>Next Week →</Btn>
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:8 }}>
      {days.map(d => {
        const k = d.toISOString().slice(0,10);
        const dayJobs = (jobsByDay[k] || []).sort((a,b) => (a.startTime||'').localeCompare(b.startTime||''));
        const dayGcal = (gcalByDay[k] || []).sort((a,b) => (a.start||'').localeCompare(b.start||''));
        const isToday = k === today;
        return <div key={k}
          onClick={()=>onDayClick(d)}
          onDragOver={e => { e.preventDefault(); e.currentTarget.style.background = G.purpleDim; }}
          onDragLeave={e => { e.currentTarget.style.background = isToday ? G.purpleDim : G.card; }}
          onDrop={e => {
            e.preventDefault();
            e.currentTarget.style.background = isToday ? G.purpleDim : G.card;
            const jobId = e.dataTransfer.getData('jobId');
            const fromDate = e.dataTransfer.getData('fromDate');
            if (jobId && fromDate !== k && onReschedule) onReschedule(jobId, k);
          }}
          style={{ ...styles.card, minHeight:480, padding:10, borderColor: isToday ? G.purpleBorder : G.cardBorder, background: isToday ? G.purpleDim : G.card, cursor:'pointer' }}>
          <div style={{ fontSize:12, color: isToday?G.purple:G.textMuted, fontWeight:700, textTransform:'uppercase', marginBottom:8 }}>{d.toLocaleDateString('en-US',{weekday:'short'})}</div>
          <div style={{ fontSize:24, fontWeight:800, marginBottom:10 }}>{d.getDate()}</div>
          {dayJobs.length === 0 && dayGcal.length === 0 && <div style={{ fontSize:12, color:G.textDim }}>+ Add job</div>}
          {dayJobs.map(j => (
            <div key={j.id}
              draggable
              onDragStart={e => { e.dataTransfer.setData('jobId', j.id); e.dataTransfer.setData('fromDate', k); e.dataTransfer.effectAllowed = 'move'; }}
              onClick={e=>{ e.stopPropagation(); onJobClick(j); }}
              style={{ ...styles.card, padding:8, marginBottom:6, cursor:'grab', borderColor: STATUS_COLOR[j.status]+'66' }}>
              <div style={{ fontSize:11, color: STATUS_COLOR[j.status], fontWeight:700 }}>{j.startTime || ''} · {j.status}</div>
              <div style={{ fontWeight:600, fontSize:13, marginTop:2 }}>{j.name}</div>
              <div style={{ fontSize:11, color:G.textMuted }}>{j.contact}</div>
              <div style={{ fontSize:11, color:G.textMuted }}>{j.tech || 'Unassigned'} · {fmtUsd(j.est||0)}</div>
            </div>
          ))}
          {dayGcal.map(e => (
            <div key={e.id} onClick={ev=>{ ev.stopPropagation(); if (e.htmlLink) window.open(e.htmlLink, '_blank'); }}
              style={{ background:'rgba(66,133,244,.12)', borderLeft:'3px solid #4285f4', padding:8, marginBottom:6, borderRadius:6, cursor:'pointer', overflow:'hidden' }}>
              <div style={{ fontSize:11, color:'#4285f4', fontWeight:700 }}>📅 GOOGLE CAL{!e.isAllDay && e.start ? ` · ${new Date(e.start).toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'})}` : ''}</div>
              <div style={{ fontWeight:600, fontSize:13, marginTop:2 }}>{e.title}</div>
              {e.location && <div style={{ fontSize:11, color:G.textMuted }}>📍 {e.location}</div>}
            </div>
          ))}
        </div>;
      })}
    </div>
  </div>;
}

function NewJobModal({ job, contacts, techs, onClose, onSaved, toast }) {
  const [form, setForm] = useState({});
  const [creatingClient, setCreatingClient] = useState(false);
  const [newClient, setNewClient] = useState({ stage:'Booked', source:'Manual', state:'FL' });
  const [calendars, setCalendars] = useState([]);
  useEffect(() => { setForm(job||{}); setCreatingClient(false); setNewClient({ stage:'Booked', source:'Manual', state:'FL' }); }, [job]);
  useEffect(() => { api('/api/calendars').then(setCalendars).catch(()=>{}); }, []);
  if (!job) return null;

  const save = async () => {
    if (!form.name) return alert('Job name required');
    let contactId = form.contactId;
    let contactName = form.contact;

    // Create new client first if needed
    if (creatingClient) {
      if (!newClient.firstName || !newClient.phone) return alert('First name and phone are required to create a client');
      try {
        const c = await api('/api/contacts', { method:'POST', body: newClient });
        contactId = c.id; contactName = `${c.firstName} ${c.lastName||''}`.trim();
      } catch(e) { return alert('Could not create client: ' + e.message); }
    }

    try {
      await api('/api/jobs', { method:'POST', body: { ...form, contactId, contact: contactName, address: form.address || (creatingClient ? newClient.address : null) } });
      onSaved();
    } catch(e) { alert(e.message); }
  };

  return <Modal open onClose={onClose} width={760}>
    <h2 style={{ margin:'0 0 14px 0' }}>+ New Job — {form.scheduledDate}</h2>

    {/* Customer picker / inline create */}
    <div style={{ ...styles.card, padding:12, marginBottom:14, borderColor: G.cyan+'44' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
        <strong style={{ fontSize:13 }}>Customer</strong>
        <Btn sz="xs" v={creatingClient?'cyan':'ghost'} onClick={()=>setCreatingClient(!creatingClient)}>
          {creatingClient ? '← Pick existing' : '+ New customer'}
        </Btn>
      </div>
      {!creatingClient ? <Select value={form.contactId||''} onChange={e => {
        const c = contacts.find(x => x.id === e.target.value);
        setForm({ ...form, contactId: e.target.value, contact: c ? `${c.firstName} ${c.lastName||''}`.trim() : '', address: c ? c.address : form.address });
      }}>
        <option value="">— select existing customer —</option>
        {contacts.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName} · {c.phone}</option>)}
      </Select>
      : <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
        <Field label="First Name *"><Input value={newClient.firstName||''} onChange={e=>setNewClient({...newClient, firstName:e.target.value})}/></Field>
        <Field label="Last Name"><Input value={newClient.lastName||''} onChange={e=>setNewClient({...newClient, lastName:e.target.value})}/></Field>
        <Field label="Phone *"><Input value={newClient.phone||''} onChange={e=>setNewClient({...newClient, phone:e.target.value})} placeholder="(813) 555-0100"/></Field>
        <Field label="Email"><Input type="email" value={newClient.email||''} onChange={e=>setNewClient({...newClient, email:e.target.value})}/></Field>
        <Field label="Address" style={{ gridColumn:'1/-1' }}><Input value={newClient.address||''} onChange={e=>setNewClient({...newClient, address:e.target.value})}/></Field>
      </div>}
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Job Name *" style={{ gridColumn:'1/-1' }}><Input value={form.name||''} onChange={e=>setForm({...form,name:e.target.value})} placeholder="e.g. AC Replacement, Annual Tune-Up"/></Field>
      <Field label="Address"><Input value={form.address||''} onChange={e=>setForm({...form,address:e.target.value})}/></Field>
      <Field label="Estimated Value"><Input type="number" value={form.est||0} onChange={e=>setForm({...form,est:Number(e.target.value)})}/></Field>
      <Field label="Date"><Input type="date" value={form.scheduledDate||''} onChange={e=>setForm({...form,scheduledDate:e.target.value})}/></Field>
      <Field label="Time"><Input type="time" value={form.startTime||''} onChange={e=>setForm({...form,startTime:e.target.value})}/></Field>
      <Field label="Duration (min)"><Input type="number" value={form.durationMinutes||120} onChange={e=>setForm({...form,durationMinutes:Number(e.target.value)})}/></Field>
      <Field label="Tech">
        <Select value={form.techId||''} onChange={e=>{
          const t = techs.find(x=>x.id===e.target.value);
          setForm({ ...form, techId:e.target.value, tech: t?.name || '' });
        }}>
          <option value="">Auto-assign later</option>
          {techs.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>
      </Field>
      <Field label="Status">
        <Select value={form.status||'Scheduled'} onChange={e=>setForm({...form,status:e.target.value})}>
          {['Scheduled','Booked','Dispatched'].map(s => <option key={s}>{s}</option>)}
        </Select>
      </Field>
      {calendars.length > 0 && <Field label="Calendar">
        <Select value={form.calendarId||''} onChange={e=>setForm({...form, calendarId:e.target.value||null})}>
          {calendars.map(c => <option key={c.id} value={c.id}>{c.name}{c.isDefault?' (default)':''}</option>)}
        </Select>
      </Field>}
      <Field label="Notes" style={{ gridColumn:'1/-1' }}><Input value={form.notes||''} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="Gate code, dog on property, special instructions..."/></Field>
    </div>

    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={save}>{creatingClient ? 'Create Customer + Schedule Job' : 'Schedule Job'}</Btn>
    </div>
  </Modal>;
}

function JobDetailModal({ job, onClose, onSaved, toast }) {
  const [advancing, setAdvancing] = useState(false);
  if (!job) return null;

  const advance = async (newStatus) => {
    setAdvancing(true);
    try {
      const body = { status: newStatus };
      if (newStatus === 'En Route') {
        const eta = prompt('ETA in minutes? (Customer gets an SMS)', '20');
        if (eta) body.etaMinutes = eta;
      }
      await api(`/api/jobs/${job.id}`, { method:'PATCH', body });
      onSaved();
    } catch(e) { alert(e.message); setAdvancing(false); }
  };

  const next = { Scheduled:'Dispatched', Booked:'Dispatched', Dispatched:'En Route', 'En Route':'On Site', 'On Site':'Completed' }[job.status];

  return <Modal open onClose={onClose} width={580}>
    <h2 style={{ margin:'0 0 4px 0' }}>{job.name}</h2>
    <Badge c={STATUS_COLOR[job.status]||G.textMuted} bg={(STATUS_COLOR[job.status]||G.textMuted)+'22'}>{job.status}</Badge>
    <div style={{ marginTop:14, display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, fontSize:13 }}>
      <Info label="Customer" value={job.contact}/>
      <Info label="Tech" value={job.tech || 'Unassigned'}/>
      <Info label="Date" value={job.scheduledDate}/>
      <Info label="Time" value={job.startTime||'—'}/>
      <Info label="Address" value={job.address||'—'} full/>
      <Info label="Est. Value" value={fmtUsd(job.est||0)}/>
      <Info label="Est. Hours" value={job.estHrs ? `${job.estHrs}h` : '—'}/>
      {job.actHrs > 0 && <Info label="Actual Hours" value={`${job.actHrs}h`}/>}
      {job.notes && <Info label="Notes" value={job.notes} full/>}
    </div>

    <div style={{ display:'flex', gap:10, marginTop:18, flexWrap:'wrap' }}>
      {next && <Btn v="primary" onClick={()=>advance(next)} disabled={advancing}>{advancing?<Spinner/>:`Advance → ${next}`}</Btn>}
      {job.status !== 'Cancelled' && job.status !== 'Completed' && <Btn v="danger" onClick={()=>advance('Cancelled')}>Cancel Job</Btn>}
      <Btn onClick={onClose}>Close</Btn>
    </div>

    <div style={{ marginTop:14, padding:10, background:G.input, borderRadius:8, fontSize:12, color:G.textMuted, lineHeight:1.5 }}>
      💡 Advancing to <strong style={{color:G.text}}>En Route</strong> automatically texts the customer with the tech's ETA + tracking link.
      <strong style={{color:G.text}}>Completed</strong> sends a thank-you and triggers the review-request workflow.
    </div>
  </Modal>;
}

function Info({ label, value, full }) {
  return <div style={{ gridColumn: full ? '1/-1' : 'auto' }}>
    <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:2 }}>{label}</div>
    <div style={{ fontSize:13 }}>{value}</div>
  </div>;
}

const CAL_COLORS = ['#a78bfa','#22d3ee','#22c55e','#f97316','#3b82f6','#ef4444','#eab308','#ec4899','#10b981','#8b5cf6'];

function CalendarsManagerModal({ calendars, onClose, onChanged, toast }) {
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState('basics');
  const blank = {
    name:'', color: CAL_COLORS[0], visibility:'account', description:'',
    autoBookMode: 'intro_only',
    bookingRules: defaultBookingRules(),
    jobTypes: [],
  };
  const save = async (cal) => {
    try {
      if (cal.id) await api(`/api/calendars/${cal.id}`, { method:'PATCH', body: cal });
      else await api('/api/calendars', { method:'POST', body: cal });
      onChanged(); setEditing(null); toast('Saved');
    } catch (e) { toast(e.message, 'error'); }
  };
  const remove = async (cal) => {
    if (!confirm(`Delete calendar "${cal.name}"? Jobs on it will move to your default calendar.`)) return;
    try { await api(`/api/calendars/${cal.id}`, { method:'DELETE' }); onChanged(); toast('Deleted'); }
    catch (e) { toast(e.message, 'error'); }
  };
  return <Modal open onClose={onClose} width={780}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
      <h2 style={{ margin:0, fontSize:18 }}>📚 Calendars</h2>
      <Btn sz="sm" v="primary" onClick={()=>{ setEditing(blank); setTab('basics'); }}>+ New Calendar</Btn>
    </div>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14, lineHeight:1.5 }}>
      Each calendar can have its own job types, booking windows, working hours, and public booking URL — same flexibility as GHL. Automated booking only books the job types you tag as "Intro / Assessment" so the AI never schedules the actual work without you approving it.
    </div>
    {!editing && calendars.map(c => (
      <div key={c.id} style={{ ...styles.card, padding:12, marginBottom:8, display:'flex', alignItems:'center', gap:12 }}>
        <span style={{ width:14, height:14, borderRadius:7, background:c.color, flexShrink:0 }}/>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:600 }}>{c.name} {c.isDefault && <Badge c={G.green} bg={G.greenLight}>default</Badge>} {c.visibility==='private' && <Badge c={G.textMuted} bg={G.input}>private</Badge>}</div>
          {c.description && <div style={{ fontSize:12, color:G.textMuted }}>{c.description}</div>}
          <div style={{ fontSize:11, color:G.textMuted, marginTop:3 }}>
            {c.jobTypes?.length ? `${c.jobTypes.length} job type${c.jobTypes.length===1?'':'s'} · ` : ''}
            Auto-book: <strong style={{ color: c.autoBookMode==='off' ? G.red : c.autoBookMode==='all' ? G.yellow : G.green }}>{c.autoBookMode === 'intro_only' ? 'intro/assessment only' : c.autoBookMode === 'all' ? 'all job types' : 'off'}</strong>
            {c.publicSlug && <> · 🔗 <a href={`/book/${c.publicSlug}`} target="_blank" rel="noreferrer" style={{ color:G.cyan }}>book/{c.publicSlug}</a></>}
          </div>
        </div>
        <Btn sz="xs" onClick={()=>{ setEditing({ ...c, bookingRules: c.bookingRules || defaultBookingRules(), jobTypes: c.jobTypes || [], autoBookMode: c.autoBookMode || 'intro_only' }); setTab('basics'); }}>Edit</Btn>
        {!c.isDefault && <Btn sz="xs" v="danger" onClick={()=>remove(c)}>Delete</Btn>}
      </div>
    ))}
    {editing && (
      <div style={{ ...styles.card, padding:0, marginTop:14, borderColor:G.purple+'66', overflow:'hidden' }}>
        <div style={{ display:'flex', borderBottom:`1px solid ${G.border}` }}>
          {['basics','availability','jobtypes','booking','sharing'].map(t => (
            <button key={t} onClick={()=>setTab(t)} style={{ flex:1, padding:'10px 14px', background: tab===t ? G.purpleDim : 'transparent', color: tab===t ? G.purple : G.textMuted, fontWeight:600, fontSize:13, border:'none', borderBottom: tab===t ? `2px solid ${G.purple}` : '2px solid transparent', cursor:'pointer', textTransform:'capitalize' }}>
              {{ basics:'1. Basics', availability:'2. Hours', jobtypes:'3. Job Types', booking:'4. Auto-Booking', sharing:'5. Public URL' }[t]}
            </button>
          ))}
        </div>
        <div style={{ padding:14 }}>
          {tab === 'basics' && <CalendarBasicsTab editing={editing} setEditing={setEditing}/>}
          {tab === 'availability' && <AvailabilityTab editing={editing} setEditing={setEditing}/>}
          {tab === 'jobtypes' && <JobTypesTab editing={editing} setEditing={setEditing}/>}
          {tab === 'booking' && <AutoBookingTab editing={editing} setEditing={setEditing}/>}
          {tab === 'sharing' && <PublicSharingTab editing={editing} setEditing={setEditing}/>}
        </div>
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end', padding:'10px 14px', borderTop:`1px solid ${G.border}`, background:G.input }}>
          <Btn onClick={()=>setEditing(null)}>Cancel</Btn>
          <Btn v="primary" disabled={!editing.name?.trim()} onClick={()=>save(editing)}>{editing.id ? 'Save' : 'Create Calendar'}</Btn>
        </div>
      </div>
    )}
  </Modal>;
}

function defaultBookingRules() {
  return {
    workingHours: {
      mon: [{ from:'08:00', to:'17:00' }], tue: [{ from:'08:00', to:'17:00' }],
      wed: [{ from:'08:00', to:'17:00' }], thu: [{ from:'08:00', to:'17:00' }],
      fri: [{ from:'08:00', to:'17:00' }], sat: [], sun: [],
    },
    minLeadMinutes: 240,
    maxAdvanceDays: 60,
    bufferMinutes: 30,
    slotDurationMinutes: 60,
    maxBookingsPerDay: 0, // 0 = unlimited
    blackoutDates: [],
  };
}

function CalendarBasicsTab({ editing, setEditing }) {
  return <>
    <Field label="Calendar Name"><Input value={editing.name} onChange={e=>setEditing({...editing, name:e.target.value})} placeholder="e.g. Installations, Service, Sales Visits"/></Field>
    <Field label="Description"><Input value={editing.description||''} onChange={e=>setEditing({...editing, description:e.target.value})} placeholder="What this calendar is for"/></Field>
    <Field label="Color">
      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
        {CAL_COLORS.map(c => (
          <button key={c} onClick={()=>setEditing({...editing, color:c})} style={{
            width:30, height:30, borderRadius:15, background:c, cursor:'pointer',
            border: editing.color === c ? '3px solid #fff' : '3px solid transparent',
            boxShadow: editing.color === c ? `0 0 0 2px ${c}` : 'none',
          }}/>
        ))}
      </div>
    </Field>
    <Field label="Visibility">
      <Select value={editing.visibility||'account'} onChange={e=>setEditing({...editing, visibility:e.target.value})}>
        <option value="account">👥 Everyone on the account</option>
        <option value="private">🔒 Only me</option>
      </Select>
    </Field>
    <Field label="Time Zone (for the booking widget display)">
      <Input value={editing.timezone||''} onChange={e=>setEditing({...editing, timezone:e.target.value})} placeholder="America/New_York (leave blank for account default)"/>
    </Field>
  </>;
}

const DOW = [['mon','Mon'],['tue','Tue'],['wed','Wed'],['thu','Thu'],['fri','Fri'],['sat','Sat'],['sun','Sun']];

function AvailabilityTab({ editing, setEditing }) {
  const rules = editing.bookingRules || defaultBookingRules();
  const setRules = (next) => setEditing({ ...editing, bookingRules: { ...rules, ...next } });
  const setDay = (dow, wins) => setRules({ workingHours: { ...(rules.workingHours||{}), [dow]: wins }});
  return <>
    <div style={{ fontSize:13, color:G.textMuted, marginBottom:10 }}>
      Set the windows when customers (or the AI) can book on this calendar. Add multiple windows per day for split shifts.
    </div>
    {DOW.map(([k, label]) => {
      const wins = rules.workingHours?.[k] || [];
      return <div key={k} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
        <div style={{ width:50, fontWeight:600, fontSize:13 }}>{label}</div>
        {wins.length === 0 && <div style={{ color:G.textMuted, fontSize:12, flex:1 }}>Closed</div>}
        {wins.map((w, i) => (
          <div key={i} style={{ display:'flex', alignItems:'center', gap:4 }}>
            <Input type="time" value={w.from} onChange={e=>{ const nw=[...wins]; nw[i]={...w, from:e.target.value}; setDay(k, nw); }} style={{ width:110 }}/>
            <span style={{ color:G.textMuted }}>–</span>
            <Input type="time" value={w.to} onChange={e=>{ const nw=[...wins]; nw[i]={...w, to:e.target.value}; setDay(k, nw); }} style={{ width:110 }}/>
            <Btn sz="xs" v="ghost" onClick={()=>setDay(k, wins.filter((_,j)=>j!==i))}>✕</Btn>
          </div>
        ))}
        <Btn sz="xs" onClick={()=>setDay(k, [...wins, { from:'09:00', to:'17:00' }])}>+ Add window</Btn>
      </div>;
    })}
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:14 }}>
      <Field label="Slot Size (min)" hint="How long each bookable slot is by default. Job types can override.">
        <Input type="number" value={rules.slotDurationMinutes||60} onChange={e=>setRules({ slotDurationMinutes:Number(e.target.value)||60 })}/>
      </Field>
      <Field label="Buffer Between Bookings (min)" hint="Drive / cleanup time before another booking can start.">
        <Input type="number" value={rules.bufferMinutes||0} onChange={e=>setRules({ bufferMinutes:Number(e.target.value)||0 })}/>
      </Field>
      <Field label="Min Lead Time (min)" hint="How far in advance customers must book. 240 = 4 hours.">
        <Input type="number" value={rules.minLeadMinutes||0} onChange={e=>setRules({ minLeadMinutes:Number(e.target.value)||0 })}/>
      </Field>
      <Field label="Max Advance (days)" hint="Furthest out customers can book.">
        <Input type="number" value={rules.maxAdvanceDays||60} onChange={e=>setRules({ maxAdvanceDays:Number(e.target.value)||60 })}/>
      </Field>
      <Field label="Max Bookings / Day" hint="0 = unlimited">
        <Input type="number" value={rules.maxBookingsPerDay||0} onChange={e=>setRules({ maxBookingsPerDay:Number(e.target.value)||0 })}/>
      </Field>
      <Field label="Blackout Dates" hint="Comma-separated YYYY-MM-DD (holidays, vacations)">
        <Input value={(rules.blackoutDates||[]).join(', ')} onChange={e=>setRules({ blackoutDates: e.target.value.split(',').map(s=>s.trim()).filter(Boolean) })} placeholder="2026-07-04, 2026-12-25"/>
      </Field>
    </div>
  </>;
}

function JobTypesTab({ editing, setEditing }) {
  const types = editing.jobTypes || [];
  const setTypes = (next) => setEditing({ ...editing, jobTypes: next });
  const add = () => setTypes([...types, { id: Math.random().toString(36).slice(2,10), name:'', durationMinutes:60, bufferMinutes:0, color: editing.color || CAL_COLORS[0], isIntro: types.length === 0, autoBookable: types.length === 0 }]);
  return <>
    <div style={{ fontSize:13, color:G.textMuted, marginBottom:10 }}>
      Job types within this calendar — e.g. "15-min Intro Call", "Free On-Site Estimate", "AC Tune-Up", "Panel Upgrade". Tag the ones the AI/widget is allowed to auto-book (typically just intros + assessments) so the AI never books real work without you.
    </div>
    {types.length === 0 && <div style={{ ...styles.card, padding:14, marginBottom:10, color:G.textMuted, fontSize:13 }}>No job types yet. Add at least one for the public booking widget to work.</div>}
    {types.map((t, i) => (
      <div key={t.id} style={{ ...styles.card, padding:12, marginBottom:8 }}>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 100px 100px 1fr', gap:8, alignItems:'end' }}>
          <Field label="Name"><Input value={t.name} onChange={e=>{ const n=[...types]; n[i]={...t, name:e.target.value}; setTypes(n); }} placeholder="e.g. Free Estimate"/></Field>
          <Field label="Duration (min)"><Input type="number" value={t.durationMinutes||60} onChange={e=>{ const n=[...types]; n[i]={...t, durationMinutes:Number(e.target.value)||60}; setTypes(n); }}/></Field>
          <Field label="Buffer (min)"><Input type="number" value={t.bufferMinutes||0} onChange={e=>{ const n=[...types]; n[i]={...t, bufferMinutes:Number(e.target.value)||0}; setTypes(n); }}/></Field>
          <Btn sz="xs" v="danger" onClick={()=>setTypes(types.filter((_,j)=>j!==i))}>Remove</Btn>
        </div>
        <Field label="Customer-facing description (optional)">
          <Input value={t.description||''} onChange={e=>{ const n=[...types]; n[i]={...t, description:e.target.value}; setTypes(n); }} placeholder="What this booking is for"/>
        </Field>
        <div style={{ display:'flex', gap:14, flexWrap:'wrap', alignItems:'center', fontSize:13 }}>
          <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
            <input type="checkbox" checked={!!t.isIntro} onChange={e=>{ const n=[...types]; n[i]={...t, isIntro:e.target.checked, autoBookable:e.target.checked ? true : t.autoBookable}; setTypes(n); }}/>
            🤝 Intro / Assessment (AI can auto-book this)
          </label>
          <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
            <input type="checkbox" checked={!!t.autoBookable} onChange={e=>{ const n=[...types]; n[i]={...t, autoBookable:e.target.checked}; setTypes(n); }}/>
            🤖 Allow auto-booking
          </label>
          <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
            <input type="checkbox" checked={!!t.requiresApproval} onChange={e=>{ const n=[...types]; n[i]={...t, requiresApproval:e.target.checked}; setTypes(n); }}/>
            ✅ Require owner approval
          </label>
        </div>
      </div>
    ))}
    <Btn sz="sm" v="cyan" onClick={add}>+ Add job type</Btn>
  </>;
}

function AutoBookingTab({ editing, setEditing }) {
  return <>
    <div style={{ fontSize:13, color:G.textMuted, marginBottom:14, lineHeight:1.55 }}>
      Controls what the AI receptionist and public booking widget are <strong style={{ color:G.text }}>allowed to schedule on their own</strong>. The safe default is <em>intro / assessment only</em> — the AI books a 15-minute consult or a free estimate, you confirm the actual work yourself.
    </div>
    <Field label="What can be auto-booked on this calendar?">
      <Select value={editing.autoBookMode||'intro_only'} onChange={e=>setEditing({...editing, autoBookMode:e.target.value})}>
        <option value="intro_only">🛡️ Intro / Assessment only (recommended)</option>
        <option value="all">⚡ All job types tagged auto-bookable</option>
        <option value="off">🚫 Off — no auto-booking on this calendar</option>
      </Select>
    </Field>
    <div style={{ ...styles.card, padding:12, background:G.input, fontSize:12, color:G.textMuted, lineHeight:1.6 }}>
      <strong style={{ color:G.text }}>Why intro-only is the default:</strong> a free 15-min phone consult or on-site estimate is low-risk to mis-schedule. Real installs / repairs need to be confirmed manually so the right tech, right equipment, and right pricing line up before anything hits the calendar.
    </div>
    <Field label="Notify on booking" hint="Who hears about new bookings made through this calendar">
      <div style={{ display:'flex', gap:14, flexWrap:'wrap', marginTop:6 }}>
        {[['email','📧 Email owner'],['sms','📱 SMS owner'],['slack','💬 Slack #leads']].map(([k, lbl]) => (
          <label key={k} style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:13 }}>
            <input type="checkbox" checked={!!editing.notifyOnBook?.[k]} onChange={e=>setEditing({ ...editing, notifyOnBook: { ...(editing.notifyOnBook||{}), [k]:e.target.checked }})}/>
            {lbl}
          </label>
        ))}
      </div>
    </Field>
  </>;
}

function PublicSharingTab({ editing, setEditing }) {
  const slug = editing.publicSlug || '';
  const url = slug ? `${typeof window !== 'undefined' ? window.location.origin : ''}/book/${slug}` : null;
  return <>
    <div style={{ fontSize:13, color:G.textMuted, marginBottom:14, lineHeight:1.55 }}>
      Give this calendar a public booking URL so leads can self-schedule from your website, SMS, email signatures, or Google Business Profile. Honors the working hours + job types + auto-booking rules on this calendar.
    </div>
    <Field label="Public booking slug" hint="Lowercase letters, numbers, dashes only. e.g. 'estimates' becomes /book/estimates">
      <Input value={slug} onChange={e=>setEditing({...editing, publicSlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g,'-').slice(0,60)})} placeholder="estimates"/>
    </Field>
    {url && (
      <div style={{ ...styles.card, padding:12, background:G.input, marginBottom:10 }}>
        <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>Your booking link</div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <a href={url} target="_blank" rel="noreferrer" style={{ color:G.cyan, fontSize:13, wordBreak:'break-all', flex:1 }}>{url}</a>
          <Btn sz="xs" onClick={()=>{ navigator.clipboard?.writeText(url); }}>📋 Copy</Btn>
        </div>
        <div style={{ marginTop:10, fontSize:12, color:G.textMuted }}>
          <strong style={{ color:G.text }}>Embed on your site:</strong>
          <pre style={{ background:G.bg, padding:8, borderRadius:6, overflow:'auto', marginTop:4, fontSize:11 }}>{`<iframe src="${url}?embed=1" width="100%" height="640" style="border:0"></iframe>`}</pre>
        </div>
      </div>
    )}
  </>;
}
